import { CanvasPage } from "./interface-studio-canvas";
import { FetchedPollData, PollQuestion, PollStatus } from "./PollSetup";

export interface PresentationPlaylistInterface {
  presentationId: string;
  documentId: string;
  title: string;
  url: string;
  views: number;
  downloads: number;
  questions: number;
  timeSpent: number;
  type: string;
  position: number;
}

export interface PresentationMetricsInterface {
  invited: number;
  attendance: number;
  presentationDuration: number;
  downloads: number;
  pollResponse: number;
  averageTimeOnSlide: number;
}

export interface PresentationAttendanceInterface {
  id: string;
  name: string;
  email: string;
  download: boolean;
  attention: number;
  timeSpent: number;
  questions: number;
}

interface PresentationSlideDataInterface {
  index: number;
  key: string;
  groupId: string | null;
  value: number;
}

export interface PresentationSlideMetricsInterface {
  docId: string;
  title: string;
  score: number;
  data: PresentationSlideDataInterface[];
}

export interface PerformanceData {
  index: number;
  key: string;
  groupId: null | string;
  value: number[];
}

export interface VistorDataInterface {
  ownerId: "";
  email: "";
  name: "";
  userId: null;
  phone: null;
  organization: null;
  jobTitle: null;
  facebook: null;
  linkedIn: null;
  converted: false;
  personId: null;
  id: "";
}

export interface PresentationPerformanceSummaryInterface {
  docId: string;
  title: string;
  downloads: number;
  attention: number;
  data: PerformanceData[];
  timeSpent: number;
}

export interface PresentationDataInterface {
  spaceId: string | null;
  friendlyId: string;
  title: string;
  thumbnail: string;
  userId: string;
  dateAdded: string;
  expiry: null | string;
  startTime: string;
  endTime: string;
  scheduledTime: string;
  visibility: boolean;
  currentSlide: number;
  allowedDomains: string[];
  passcode: string;
  status: number;
  authMode: number;
  type: number;
  canDownload: number;
  questionsAllowed: boolean;
  deleted: boolean;
  currentItem: string;
  duration: number;
  questions: number;
  visitors: number;
  views: number;
  id: string;
  requestPasscode: boolean;
  waitingRoom: boolean;
  watermark: string;
  unlocked?: UnlockedInterface;
  isEvent: boolean;
  eventId: string;
  projectorControl: boolean;
  displayInstruction: number;
  bannerPosition: number;
  bannerStyle: number;
  itemCount: number;
  dateModified?: string;
  eventCount: number;
  summary: string | null;
}

export interface PresentationDocInterface {
  sessionId: string;
  sessionType: number;
  documentId: string;
  title: string;
  note: string | null;
  position: number;
  url: string;
  type: string;
  deleted: boolean;
  id: string;
  activeIndex?: number;
}

export interface InnerPresentationInterface {
  presentation: PresentationDataInterface;
  doc: PresentationDocInterface[];
}

interface ZoomAuthInterface {
  status: string;
  upgradable: boolean;
}

export interface ZoomConfigInterface {
  clientVersion: string;
  browserVersion: string;
  userAgent: string;
  auth: ZoomAuthInterface;
  unsupportedApis: any[];
  runningContext: string;
}

export interface MeetConfigInterface {
  codeChallenge: string;
  state: string;
  sessionId: string;
}

export interface UserContextInterface {
  status: "unauthenticated" | "authenticated" | "authorized";
  screenName: string;
  participantId: string;
  participantUUID: string;
  role: string;
}

export interface SlideInterface {
  id: string;
  url: string;
  position: number;
  title: string;
  note: string;
  imgUrl: string;
  unlocked?: boolean;
  type?: string;
  textContent?: string;
  page?: CanvasPage;
  // rowKey?: string; This updated to id for html docs on 25/06/2025
  attributes?: {
    section?: Record<string, string>; // e.g. 'data-transition', 'data-background-gradient', etc.
    video?: Record<string, string>; // e.g. 'data-autoplay', 'src'
    iframe?: Record<string, string>; // e.g. 'src', 'data-allowfullscreen'
    // if you ever need overlays, captions, etc:
    [otherTag: string]: Record<string, string> | undefined;
  };
}

export interface DocDetailsInterface {
  slides: SlideInterface[];
  id: string;
  parentId: null | string;
  folderId: string;
  url: string;
  title: string;
  note: null | string;
  documentId: string;
  dateAdded?: string;
  position: number;
}

export type StudioActionType =
  | "wordcloud"
  | "quiz"
  | "multichoice"
  | "default"
  | "iframe"
  | "videoIframe"
  | "test"
  | "canvas"
  | "video"
  | "loading";

export interface UserInterface {
  name: string;
  email: string;
  status: number;
  avatar: string;
}

export interface UnlockedInterface {
  [key: string]: number[];
}

export interface EditableObjectInterface {
  expiry: string | null;
  canDownload: number;
  authMode: number;
  enableWaitingRoom: boolean;
  title: string;
  passcode: string;
  watermark: string;
  allowQuestion: boolean;
  domains: string[];
  projectorControl: boolean;
  displayInstruction: number;
  bannerPosition: number;
  bannerStyle: number;
}

export interface PresentationDisplayInterface {
  [key: string]: SlideInterface[];
}

export interface PresentationDisplayDetailsRefInterface {
  [key: string]: (FetchedPollData | SlideInterface)[] | FetchedPollData;
}

export interface PresentationDisplayInterfaceNew {
  [key: string]: (FetchedPollData | SlideInterface)[] | FetchedPollData;
}

export interface DateAgendaInterface {
  date: string;
  title: string;
  sessions: AgendaInterface[];
}

export enum EventStatus {
  Scheduled,
  Started,
  Ended,
}

export interface EventsInterface {
  title: string;
  friendlyId: string | null;
  description: string;
  startDate: string;
  dateCreated: string;
  dateModified: string;
  createdBy: string;
  modifiedBy: string;
  teamId: string;
  published: boolean;
  visibility: boolean;
  waitingRoom: boolean;
  restriction: number;
  domains: null | string[];
  canDownload: number;
  questionsAllowed: boolean;
  watermark: null | string;
  authMode: number;
  id: string;
  status: number;
  currentSession: null | string;
  agenda: AgendaInterface[];
  role: "EventOwner" | "Collaborator" | "Participant";
  banner: string;
  newInvite: boolean;
  currentPresentation: string | null;
  expiry: string | null;
  projectorControl: boolean;
  displayInstruction: number;
  bannerPosition: number;
  bannerStyle: number;
  passcode: string | null;
  category: null | string;
  timeStarted: string;
  timeEnded: string;
  checkedInVote: boolean;
  hasJudges: boolean;
  isPublic: boolean;
  enableSummary: boolean;
  endDate?: string;
  eventLink?: EventLinkInterface[];
  timezone?: string;
  currency?: string;
  minimumTicket?: string;
  maximumTicket?: string;
  longitude?: number;
  latitude?: number;
  eventLocation?: EventLocation;
  currentDay?: string;
  dayAgenda?: string;
  bannerUrl?: string | null;
  summary?: string | null;
  summarySent?: boolean;
  authorizedRequest?: boolean;
  organizerId?: string | null;
  rating?: number;
  attendees?: number;
  engagementScore?: number;
  ratingCount?: number;
  quickPresentation?: boolean;
}

export interface SocialsInterface {
  platform: string;
  url: string;
}

export interface OrganizerDetails {
  id: string;
  name: string;
  socials: SocialsInterface[];
  organizer: boolean;
  imageUrl?: string;
}
export interface NewEventsInterface {
  title: string;
  friendlyId: string | null;
  description: string;
  startDate: string;
  dateCreated: string;
  dateModified: string;
  createdBy: string;
  modifiedBy: string;
  teamId: string;
  published: boolean;
  visibility: boolean;
  waitingRoom: boolean;
  restriction: number;
  domains: null | string[];
  canDownload: number;
  questionsAllowed: boolean;
  watermark: null | string;
  authMode: number;
  id: string;
  status: EventStatus;
  currentSession: null | string;
  agenda: DateAgendaInterface[];
  role: "EventOwner" | "Collaborator" | "Participant";
  banner: string;
  newInvite: boolean;
  currentPresentation: string | null;
  expiry: string | null;
  projectorControl: boolean;
  displayInstruction: number;
  bannerPosition: number;
  bannerStyle: number;
  passcode: string | null;
  category: null | string;
  timeStarted: string;
  timeEnded: string;
  checkedInVote: boolean;
  hasJudges: boolean;
  isPublic: boolean;
  enableSummary: boolean;
  endDate?: string;
  eventLink?: EventLinkInterface[];
  timezone?: string;
  currency?: string;
  minimumTicket?: number;
  maximumTicket?: number;
  longitude?: number;
  latitude?: number;
  organizerId?: string;
  eventLocation?: EventLocation;
  currentDay?: string;
  allowPostEventAccess?: boolean;
  organizer?: OrganizerDetails;
}

export interface EventsPaginationInterface {
  totalData: number;
  pageLimit: number;
  currentPage: number;
  currentPageData: number;
  totalPages: number;
  prevPage: number | null;
  nextPage: number | null;
}

export interface EventsFilterInterface {
  query: string | null;
  startDate: string | null;
  endDate: string | null;
  page: number;
  pageLimit: number;
  range: number;
  sortBy: string | null;
  sortDirection: number;
}

export interface EventsResponseInterface {
  message: string;
  status: string;
  data: {
    pagination: EventsPaginationInterface;
    events: EventsInterface[];
    filter: EventsFilterInterface;
  };
}

export interface CollaboratorsInterface {
  sessionId: string;
  presentationId: string;
  eventId: string;
  eventFriendlyId: null | string;
  email: string;
  role: string;
  status: string;
  userId: string;
  fullName: string;
  id: string;
}

export interface AgendaPresentationInterface {
  presentation: PresentationDataInterface;
  doc: DocDetailsInterface[];
}

export interface AgendaInterface {
  eventId: string;
  position: number;
  title: string;
  time: string;
  duration: number;
  presentationId: string;
  collaborators: CollaboratorsInterface[];
  speakers: SpeakerInterface[];
  presentation: AgendaPresentationInterface;
  id: string;
  description: string;
  agendaType: AgendaType;
  start?: string;
  children?: AgendaInterface[];
  avatarUrl?: string | null;
  debrief?: number;
  visibility?: boolean;
  parentId?: string;
  pitchId?: string;
}

export interface AgendaInterfaceUpdates extends AgendaInterface {
  convertedStartTime: string;
  convertedEndTime: string;
}

// Polls
export type PollProgress = "StartPoll" | "timer" | "response" | "leaderboard";

export interface AllPollsRecords {
  [key: string]: {
    pollTaken: boolean;
    questions: (PollQuestion & {
      hasBeenAsked: boolean;
    })[];
  };
}

export interface PollControl {
  reset: boolean;
  stop: boolean;
  resetTimer: boolean;
  viewLeaderboard: boolean;
}

export interface PollsRecordOptions {
  pollTaken: boolean;
  showLeaderboard: boolean;
  questions: (PollQuestion & {
    hasBeenAsked: boolean;
    hasQuestionDurationElapsed: boolean; // this is to check if the question duration has elapsed
  })[];
}

export interface PollsRecord {
  [key: string]: PollsRecordOptions;
}

export interface ViewersPollsRecord {
  [key: string]: PollsRecord;
}

export interface ViewerPollsRecordOptions {
  [key: string]: {
    totalScore: number;
    questions: (PollQuestion & {
      responded: boolean; // this should be updated even when the user did not respond and the duration elaspsed
      indexOfOptionSelected: number | null;
      score: number;
    })[];
  };
}

export interface ViewerPollsRecord {
  username: string;
  connectionId: string;
  presentaionId: string;
  avatarId: string;
  joined: boolean; // updates to boolean when the user joins the poll - basically when they send a request to join the poll --- I can also use this to add user automatically to the next poll
  polls: ViewerPollsRecordOptions;
}

export interface ProjectorInterface {
  sessionId: string;
  code: string;
  connectionId: string;
  presentaConnectionId: string;
  valid: boolean;
  platform: string;
  browser: string;
  ip: string;
  status: number;
  id: string;
}

export interface ProjectorSetting {
  color: string;
  description: string;
  preferredLayout: number;
  username: string;
  textColor: string;
  fullName: string;
  alias: string;
}

// Poll V2
export interface PollParticipantScoreBoard {
  avatar: string;
  displayName: string;
  id: string;
  pollId: string;
  presentaConnectionId: string;
  score: number;
  userId: string;
  prevScore: number;
  email?: string;
}

interface Response {
  responseId: string[];
  answer: string[];
}

export interface PollResponse {
  partitionKey: string;
  rowKey: string;
  questionId: string;
  question: string;
  userId: string;
  timestamp: string;
  eTag: string;
  responseTime: number;
  timeAnswered: string;
  responseJson: string;
  response: Response;
  answeredBy: string;
}

export interface PollResponsesEvent {
  EventName: string;
  presentationId: string;
  pollId: string;
  responses: PollResponse[];
}

export type GroupedResponses = {
  [questionId: string]: PollResponse[];
};

export interface ProjectorRequestInterface {
  sessionId: string;
  code: string;
  connectionId: string;
  presentaConnectionId: string;
  valid: string;
  platform: string;
  browser: string;
  ip: string;
  ownerId: string;
  requestedBy: string;
  status: number;
  id: string;
}

// V3

export interface QuestionProgress {
  questionId: string;
  question: PollQuestion;
  status: null | "Ended" | "Started";
}

export interface PollProgressStatus {
  status: PollStatus;
  questions: QuestionProgress[];
}

export interface PresenterPollProgress {
  [key: string]: PollProgressStatus;
}

// Viewer Poll progress

export interface ViewerQuestionProgress {
  questionId: string;
  question: PollQuestion;
  status: null | "Ended" | "Started";
  answered: boolean;
}

export interface ViewerPollProgressStatus {
  status: PollStatus;
  questions: ViewerQuestionProgress[];
}

export interface ViewerPollProgress {
  [key: string]: ViewerPollProgressStatus;
}

export interface Root {
  single: boolean;
  data: Daum[];
}

export interface Daum {
  aiSummary: string;
  personal_Emails: string[];
  phone_Numbers: string[];
  job_Title_Levels: string[];
  interests: string[];
  skills: string[];
  location_Name: string;
  street_Addresses: any[];
  regions: any[];
  countries: string[];
  emails: Email[];
  experience: Experience[];
  education: Education[];
  profiles: Profile[];
  id: string;
  personDataId: string;
  full_Name: string;
  first_Name: string;
  middle_Initial: any;
  middle_Name: any;
  last_Initial: string;
  last_Name: string;
  sex: string;
  birth_Year: any;
  birth_Date: any;
  linkedin_Url: string;
  linkedin_Username: string;
  linkedin_Id: string;
  facebook_Url: string;
  facebook_Username: string;
  facebook_Id: string;
  twitter_Url: any;
  twitter_Username: any;
  github_Url: any;
  github_Username: any;
  work_Email: any;
  recommended_Personal_Email: string;
  mobile_Phone: string;
  industry: string;
  job_Title: string;
  job_Title_Role: any;
  job_Title_Sub_Role: any;
  job_Onet_Code: any;
  job_Onet_Major_Group: any;
  job_Onet_Minor_Group: any;
  job_Onet_Broad_Occupation: any;
  job_Onet_Specific_Occupation: any;
  job_Onet_Specific_Occupation_Detail: any;
  job_Company_Id: string;
  job_Company_Name: string;
  job_Company_Website: string;
  job_Company_Size: string;
  job_Company_Founded: any;
  job_Company_Industry: string;
  job_Company_Linkedin_Url: string;
  job_Company_Linkedin_Id: string;
  job_Company_Facebook_Url: string;
  job_Company_Twitter_Url: string;
  job_Company_Type: any;
  job_Company_Ticker: any;
  job_Company_Location_Name: string;
  job_Company_Location_Locality: any;
  job_Company_Location_Metro: any;
  job_Company_Location_Region: string;
  job_Company_Location_Geo: any;
  job_Company_Location_Street_Address: any;
  job_Company_Location_Address_Line2: any;
  job_Company_Location_Postal_Code: any;
  job_Company_Location_Country: string;
  job_Company_Location_Continent: string;
  job_Company_Employee_Count: any;
  job_Company_Inferred_Revenue: any;
  job_Company_12Mo_Employee_Growth_Rate: any;
  job_Company_Total_Funding_Raised: any;
  job_Last_Changed: string;
  job_Last_Verified: string;
  job_Last_Updated: any;
  job_Start_Date: string;
  job_Summary: any;
  location_Locality: any;
  location_Metro: any;
  location_Region: any;
  location_Country: string;
  location_Continent: string;
  location_Street_Address: any;
  location_Address_Line2: any;
  location_Postal_Code: any;
  location_Geo: any;
  location_Last_Updated: string;
  linkedin_Connections: any;
  facebook_Friends: any;
  inferred_Salary: any;
  inferred_Years_Experience: any;
  summary: any;
  name_Aliases: any;
  possible_Street_Addresses: any;
  possible_Location_Names: any;
  possible_Birth_Dates: any;
  certifications: any;
  languages: any;
  first_Seen: any;
  num_Sources: any;
  num_Records: any;
  version_Status: any;
}

export interface Email {
  id: string;
  personDataId: any;
  address: string;
  type: string;
  firstSeen: any;
  lastSeen: any;
  numSources: any;
}

export interface Experience {
  id: any;
  personDataId: any;
  personData: any;
  company: Company;
  locationNames: any;
  endDate: any;
  startDate: any;
  title: Title;
  isPrimary: boolean;
  summary: any;
  numSources: any;
  firstSeen: any;
  lastSeen: any;
}

export interface Company {
  id?: string;
  personDataId: any;
  personData: any;
  name: string;
  size?: string;
  founded?: number;
  industry?: string;
  location?: Location;
  linkedinUrl: any;
  linkedinId: any;
  facebookUrl: any;
  twitterUrl: any;
  website?: string;
  ticker: any;
  type: any;
  raw: any;
  fuzzyMatch: boolean;
}

export interface Location {
  id: any;
  personDataId: any;
  personData: any;
  name: string;
  locality?: string;
  region: string;
  metro: any;
  country: string;
  continent: string;
  streetAddress: any;
  addressLine2: any;
  postalCode: any;
  geo?: string;
}

export interface Title {
  id: any;
  personDataId: any;
  personData: any;
  name: string;
  raw: any;
  role?: string;
  subRole: any;
  levels: string[];
}

export interface Education {
  id: string;
  personDataId: any;
  personData: any;
  school?: School;
  startDate: any;
  endDate: any;
  gpa: any;
  raw: any;
  summary: any;
  degrees: string[];
  majors: string[];
  minors: any[];
}

export interface School {
  id: string;
  educationId: any;
  education: any;
  name: string;
  type: string;
  linkedinUrl: any;
  facebookUrl: any;
  twitterUrl: any;
  linkedinId: any;
  website: string;
  domain: string;
  raw: any;
}

export interface Profile {
  id: string;
  personDataId: any;
  personData: any;
  network: string;
  url: string;
  username: string;
  numSources: any;
  firstSeen: any;
  lastSeen: any;
}

export interface VisitorDataInterface {
  ownerId: string;
  email: string;
  name: string;
  userId: null | string;
  phone: null | string;
  organization: null | string;
  jobTitle: null | string;
  facebook: null | string;
  linkedIn: null | string;
  converted: boolean;
  personId: null | string;
  id: string;
}

interface innerQuestionResponse {
  responseId: string[];
  answer: string[];
}

export interface QuestionResponseType {
  answeredBy: string;
  eTag: string;
  partitionKey: string;
  question: string;
  questionId: string;
  response: innerQuestionResponse;
  answer: string[];
  responseId: string[];
  responseJson: string;
  responseTime: number;
  rowKey: string;
  timeAnswered: string;
  timestamp: string;
  userId: string;
}

export interface ListInterface {
  ownerId: string;
  title: string;
  description: string;
  userCount: number;
  dateUpdated: string;
  id: string;
}

export interface ActivityInterface {
  userId: string;
  action: string;
  description: string;
  actionId: string;
  activityType: string;
  actionTime: string;
}

export interface ListContactInterface {
  ownerId: string;
  contactId: string;
  listId: string;
  title: string;
  dateAdded: string;
  email: string;
  name: string;
  userId: null | string;
  phone: null | string;
  id: string;
}

export interface TestimonialReviewInterface {
  questionId: string;
  question: string;
  comment: string;
  score: number;
}

export interface TestimonialInterface {
  id: string;
  accessType: number;
  date: string;
  email: string;
  event: string;
  eventId: string;
  feedback: string;
  firstName: string;
  fullName: string;
  lastName: string;
  presentation: string;
  presentationId: string;
  rating: number;
  reviews: TestimonialReviewInterface[];
  approved: boolean;
  jobTitle: string;
  profilePic: string;
}

export interface EventUserProfileInterface {
  name: string;
  email: string;
  eventRole: "Judge" | "Guest";
  checkedIn: boolean;
}

export interface ReviewQuestionInterface {
  eventId: string;
  question: string;
  dateCreated: string;
  id: string;
  questionId: string;
  type: number;
}

export interface ReviewUserQuestionInterface {
  dateCreated: string;
  id: string;
  ownerId: string;
  question: string;
  selected: boolean;
  type: number;
}

export interface EachReviewInterface {
  questionId: string;
  question: string;
  comment: string;
  score: number;
}

export interface EventsReviewInterface {
  presentationId: string;
  eventId: string;
  presentation: string;
  event: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  feedback: string;
  rating: number;
  date: string;
  accessType: string;
  reviews: EachReviewInterface[];
}

export interface RatingFormat {
  rating: number;
  myVotes: number;
  allVotes: number;
}

export interface InnerPresentationRatingInterface {
  presentationId: string;
  presentation: string;
  rating: RatingFormat;
  guestsRating: RatingFormat;
  judgesRating: RatingFormat;
}

export interface PresentationRatingSummaryInterface {
  overallRating: number;
  judgesRating: number;
  participantRating: number;
  feedbacks: number;
  testimonials: number;
}

export interface PresentationRatingInterface {
  data: InnerPresentationRatingInterface[];
  summary: PresentationRatingSummaryInterface;
}

export interface EventSettingsPayload {
  eventId: string;
  teamId: string;
  allowQuestion: boolean;
  enableWaitingRoom: boolean;
  enableSummary: boolean;
  enableConversation: boolean;
  authMode: number;
  projectorControl: boolean;
  displayInstruction: number;
  bannerPosition: number;
  bannerStyle: number;
  checkedInVote: boolean;
  hasJudges: boolean;
}

export interface DiscussionInterface {
  presentationId: string;
  slideId: string;
  slideTitle: string;
  userId: string;
  name: string;
  request: string;
  answer: string | null;
  askTime: string;
  answeredTime: null | string;
  isAnswered: boolean;
  isQueued: boolean;
  discussing: boolean;
  likes: number;
  dislikes: number;
  voted: number | null;
  id: string;
  avatar?: string;
  responses: number;
}

export interface AttendeesNoteInterface {
  presentationId: string;
  documentId: string;
  slideId: string;
  userId: string;
  note: string;
  lastModified: string;
  partitionKey: string;
  rowKey: string;
  eTag: string;
  timestamp: string;
  id: string;
}

export interface ReactionInterface {
  Clap: number;
  ThumbsUp: number;
  Heart: number;
  DrumRoll: number;
}

export interface CommentInterface {
  presentationId: string;
  documentId: string;
  slideId: string;
  questionId: string;
  userId: string;
  comment: string;
  date: string;
  partitionKey: string;
  rowKey: string;
  eTag: string;
  timestamp: string;
  id: string;
  name: string;
}

export interface PeopleReactionInterface {
  presentationId: string;
  documentId: string;
  slideId: string;
  eventId: string;
  userId: string;
  reaction: number;
  date: string;
  partitionKey: string;
  rowKey: string;
  eTag: string;
  timestamp: string;
  id: string;
}

export interface PollOptionInterface {
  rank: number;
  text: string;
  imageUrl: string;
  shape: string;
  color: string;
  isSelected: boolean;
}

export interface CreatePollInterface {
  questionType: string;
  rank: number;
  question: string;
  questionImageUrl: string;
  answer: string;
  status: string;
  duration: number;
  showLeaderboard: boolean;
  showPercentage: boolean;
  showAnswer: boolean;
  questionBgColor: string;
  responsePerUser: number;
  colors: string[];
  options: PollOptionInterface[];
}

export interface VideoSettings {
  autoPlay: boolean;
  mute: boolean;
  allowControl: boolean;
  allowDownload: boolean;
  playOnAudienceScreen: boolean;
}

export interface IframeSettings {
  autoPlay: boolean;
  allowControl: boolean;
  customEmbed?: boolean;
}

export type HandleSettingsType = "video" | "iframe";

export type SettingsMap = {
  video: VideoSettings;
  iframe: IframeSettings;
};
export interface PollInformation {
  partitionKey: string;
  rowKey: string;
  timestamp: string;
  eTag: string;
  title: string;
  owner: string;
  ownerId: string;
  presentationId: string;
  createdDate: string | null;
  launchDate: string;
  startTime: null | string;
  endTime: null | string;
  status: number;
  currentQuestion: null | string;
  jsonQuestions: string;
  position: number;
  questions: CreatePollInterface[];
}

export interface CreateNewPollQuestionInterface {
  presentationId: string;
  eventId: string;
  documentId: string;
  title: string;
  startTime: string;
  position: number;
  questions: CreatePollInterface[];
}

export interface DocumentFromLibrary {
  id: string;
  url: string;
  title: string;
  access: string | null;
  isFolder: boolean;
  folderId: string;
  lastUpdated: string;
}

export interface VariantType {
  id: string;
  eventId: string;
  formId: string | null;
  name: string;
  price: number;
  quantity: number;
  quantitySold: number;
  description: string;
  saleStarts: string;
  saleEnd: string;
  requireApproval: boolean;
  isAvailable: boolean;
  dateCreated: string;
  variants: string | null;
}

export type ActivePresentationDocumentType = "default" | "poll" | "html";

export interface TicketInterface {
  id: string;
  dateCreated: string;
  description: string;
  name: string;
  price: number;
  isAvailable: boolean;
  saleStarts: string;
  saleEnd: string;
  quantity: number;
  formId?: string;
  parentId?: string;
  requireApproval?: boolean;
  variants?: VariantType[];
  currency?: string;
  quantitySold?: number;
  selectedQuantities?: number;
  dateExpired?: boolean;
  actualQuantitySold?: number;
  active?: boolean;
}

export interface FieldInterface {
  label: string;
  type: number;
  options: string[];
  required: boolean;
  id: string;
}

export interface FormInterface {
  id: string;
  dateCreated: string;
  description: string;
  title: string;
  fields: FieldInterface[];
}

export enum FormType {
  TextBox,
  Password,
  RichText,
  MultiChoice,
  RadioButton,
  DropDown,
  Number,
  Date,
  Range,
  LinearScale,
}

export interface CreateLineUpPayload {
  name: string;
  email: string;
  avatar: string;
  description: string;
  facebook: string;
  instagram: string;
  linkedIn: string;
  youtube: string;
}

export interface SpeakerInterface {
  id: string;
  category: string;
  categoryId: string;
  speakerId: string;
  avatar: null | string | File;
  eventId: string;
  email: string;
  name: string;
  description: string;
  facebook: string;
  instagram: string;
  linkedIn: string;
  youtube: string;
  otherPlatforms: null | string;
  avatarIndex?: number;
  speaker?: string;
  lineUpType?: number;
  designation?: string;
  company?: string;
  imageUrl?: string;
}

export interface LineUpInterface {
  category: string;
  categoryId: string;
  speakers: SpeakerInterface[];
  sortOrder: number;
}

export interface LineUpCategory {
  name: string;
  eventId: string;
  dateAdded: string;
  id: string;
}

export interface OverviewInterface {
  agenda: number;
  lineups: number;
  invites: number;
  acceptedInvites: number;
  judges: number;
  ticketOrders: number;
}

export interface AgendaReportInterface {
  invites: number;
  acceptedInvites: number;
  withDocuments: number;
}

export enum SpeakerType {
  Speaker,
  Judge,
  Panelist,
  Moderator,
  Contestant,
  Pitch,
}

export type AgendaType =
  | "Keynote"
  | "Plenary"
  | "Panel Discussion"
  | "Workshop"
  | "Training"
  | "Competition/Pitch"
  | "Fireside Chat"
  | "Breakout/Roundtable"
  | "Networking Session"
  | "Exhibition/Trade Show"
  | "Awards Ceremony/Gala"
  | "Q&A/AMA"
  | "Poster Session"
  | "Product Demo/Launch";

export interface TicketSummary {
  all: number;
  gross: number;
  reserved: number;
  sold: number;
  salesToday?: number;
  quantitySoldToday: number;
}

export interface TicketOrder {
  date: string;
  paid: boolean;
  currency: string;
  amount: number;
  eventId: string;
  orderedBy: string;
  orderName: string;
  phoneNumber: string;
  paymentLink: null | string;
  paymentReference: string | null;
  approved: boolean;
  orderQuantity: number;
  status: number;
  tickets: {
    unitPrice: number;
    ticketId: string;
    orderQuantity: number;
  }[];
  id: string;
}

export interface OrderTicketInterface {
  paymentMade: boolean;
  paymentSecret: string;
  amount: number;
  reference: string;
  paymentOptions: string[];
  paymentType: string;
}

export interface OrderDetails {
  date: string;
  paid: boolean;
  currency: string;
  amount: number;
  eventId: string;
  orderedBy: string;
  orderName: string;
  phoneNumber: string;
  paymentLink: string | null;
  paymentReference: string | null;
  approved: boolean;
  orderQuantity: number;
  status: number;
  tickets: {
    unitPrice: number;
    ticketId: string;
    orderQuantity: number;
  }[];
  id: string;
}

export interface SponsorCategory {
  dateAdded: string;
  eventId: string;
  id: string;
  name: string;
  sortOrder: number;
}

export interface SponsorInterface {
  categoryId: string;
  sponsorId: string;
  category: string;
  avatar: string;
  eventId: string;
  name: string;
  description: string;
  sortOrder: number;
  categoryOrder: number;
  id: string;
  visible: boolean;
  url: string;
}

export interface AllSponsors {
  category: string;
  categoryId: string;
  sortOrder: number;
  sponsors: SponsorInterface[];
}

export enum TickeStatus {
  Pending,
  Approved,
  Declined,
  Paid,
}

export type AddSpeakerModalType =
  | "edit"
  | "create"
  | "disabled"
  | "showOption"
  | "pick-from-existing";

export type SpeakerModalSource = "lineup" | "agenda" | "pitch";

export interface SlotType {
  title: string;
  collaborators: string[];
  time?: string | null;
  duration?: number | null;
  collaboratorId?: string | null;
  position?: number;
  id?: string;
  start?: string | null;
  end?: string | null;
  agendaType: AgendaType;
  speakers?: SpeakerInterface[];
  description: string;
  children?: AgendaInterface[];
  debrief?: number | null;
  computedEnd?: Date;
  computedStart?: Date;
}

export interface SortPayload {
  itemId: string;
  position: number;
}

export interface OrganisationSocial {
  facebook: null | string;
  x: null | string;
  instagram: null | string;
  linkedIn: null | string;
  website: null | string;
  calendly: null | string;
  email?: null | string;
  phone?: null | string;
}
export interface OrganisationInterface {
  address: string;
  contactEmail: null | string;
  contactPhone: null | string;
  description: string;
  id: string;
  logoUrl: string;
  name: string;
  pictureUrl: string;
  socials: OrganisationSocial;
}

export interface SpeakerProfileBadge {
  color: string;
  label: string;
  badgeUrl: string;
}

export interface SpeakerProfileUpcomingEvent {
  title: string;
  description: string;
  days: number;
  hours: number;
  mins: number;
  secs: number;
}

export interface SpeakerProfilePastEvent {
  title: string;
  imageUrl: string;
  rating: string;
  attendees: string;
}

export interface SpeakerProfileTestimonial {
  text: string;
  name: string;
  handle: string;
  avatarUrl: string;
}

// export interface SpeakerProfileInterface {
//   id: string;
//   name: string;
//   title: string;
//   location: string;
//   availability: string;
//   rating: string;
//   reviewCount: number;
//   priceRange: string;
//   images: string[];
//   bio: string;
//   badges: SpeakerProfileBadge[];
//   expertise: string[];
//   videoUrl: string;
//   videoThumbnailUrl: string;
//   pastEvents: SpeakerProfilePastEvent[];
//   testimonials: SpeakerProfileTestimonial[];
//   upcomingEvent: SpeakerProfileUpcomingEvent | null;
//   socials: OrganisationSocial;
//   // images: File[];
// }

interface SocialMediaItem {
  platform: string;
  url: string;
}
export interface SpeakerSettingsPayload {
  name: string;
  country: string;
  description: string;
  images: string[];
  imagesId: string[]; // adding from the library
  videoUrls: string[];
  socialMedia: SocialMediaItem[];
  designation: string;
  company: string;
  expertise: string[];
  phone: string;
  rating?: number; // Read-only field from response (0-5 scale)
  currency: string;
  minimumAmount: number;
  maximumAmount: number;
  availableCountries: string[];
  contactEmail: string;
}

export interface SpeakerSetupWithResumePayload {
  resume: File;
}

export type SpeakerSetupWithResumeResponse = SpeakerSettingsResponse;

export interface ConnectedAppsInterface {
  userId: string;
  app: AppType;
  type: AppSection;
  properties: string;
  accountId: string;
  email: string;
  connected: boolean;
  enabled: boolean;
  id: string;
}

export enum AppSection {
  Meetings,
  Payments,
  Events,
}

export enum AppType {
  Zoom,
  Microsoft_Teams,
  Google_Meet,
  Eventbrite,
  Stripe,
  Flutterwave,
}

export interface EventBrite {
  title: string;
  friendlyId: string;
  description: string;
  category: null | string;
  scheduledDate: string;
  scheduledEndDate: string;
  timezone: string;
  dateCreated: string;
  dateModified: string;
  timeStarted: string | null;
  timeEnded: string | null;
  createdBy: string;
  modifiedBy: string;
  banner: string;
  teamId: string;
  published: boolean;
  deleted: boolean;
  visibility: boolean;
  waitingRoom: boolean;
  restriction: number;
  domains: string | null;
  canDownload: number;
  questionsAllowed: boolean;
  watermark: string | null;
  authMode: number;
  projectorControl: boolean;
  displayInstruction: number;
  status: number;
  bannerPosition: number;
  bannerStyle: number;
  currentSession: string | null;
  currentPresentation: string | null;
  enableSummary: boolean;
  enableConversation: boolean;
  summary: string | null;
  summarySent: boolean;
  hasJudges: boolean;
  checkedInVote: boolean;
  eventLink: string | null;
  latitude: string | null;
  longitude: string | null;
  role: string | null;
  newInvite: boolean;
  id: string;
}

export interface PaginationData {
  totalData: number;
  pageLimit: number;
  currentPage: number;
  currentPageData: number;
  totalPages: number;
  prevPage: null | number;
  nextPage: number | null;
}

export interface FilterObject {
  query: null | string;
  startDate: null | string;
  endDate: null | string;
  page: number;
  pageLimit: number;
  range: number;
  sortBy: null | number;
  sortDirection: number;
}

export interface OrganizationDetails {
  userId: string;
  friendlyId: string;
  name: string;
  address: string;
  description: string;
  contactEmail: null | string;
  contactPhone: null | string;
  pictureUrl: string;
  logoUrl: string;
  dateModified: string;
  facebook: string;
  x: string;
  instagram: string;
  linkedIn: string;
  calendly: string;
  website: string;
  id: string;
}

export interface EventBriteData {
  name: {
    text: string;
    html: string;
  };
  description: {
    text: string;
    html: string;
  };
  url: string;
  start: {
    timezone: string;
    local: string;
    utc: string;
  };
  end: {
    timezone: string;
    local: string;
    utc: string;
  };
  organization_id: string;
  created: string;
  changed: string;
  capacity: number;
  capacity_is_custom: boolean;
  status: string;
  currency: string;
  listed: boolean;
  shareable: boolean;
  invite_only: boolean;
  online_event: boolean;
  show_remaining: boolean;
  tx_time_limit: number;
  hide_start_date: boolean;
  hide_end_date: boolean;
  locale: string;
  is_locked: boolean;
  privacy_setting: string;
  is_series: boolean;
  is_series_parent: boolean;
  inventory_type: string;
  is_reserved_seating: boolean;
  show_pick_a_seat: boolean;
  show_seatmap_thumbnail: boolean;
  show_colors_in_seatmap_thumbnail: boolean;
  source: string;
  is_free: boolean;
  version: null | string;
  summary: string;
  facebook_event_id: null | string;
  logo_id: string;
  organizer_id: string;
  venue_id: null | string;
  category_id: string;
  subcategory_id: string;
  format_id: string;
  id: string;
  resource_uri: string;
  is_externally_ticketed: boolean;
  logo: {
    crop_mask: {
      top_left: {
        x: number;
        y: number;
      };
      width: number;
      height: number;
    };
    original: {
      url: string;
      width: number;
      height: number;
    };
    id: string;
    url: string;
    aspect_ratio: string;
    edge_color: string;
    edge_color_set: boolean;
  };
  tickets: null | TicketInterface;
}

export type EventsTeamRole =
  | "Event Admin"
  | "Content Manager"
  | "Schedule Manager"
  | "FrontDesk";

export interface EventsTeamInterface {
  name: string;
  email: string;
  role: EventsTeamRole;
  dateCreated: string;
}

export const enum EventScope {
  All,
  Organized,
  Featured,
  Attended,
  Speaking,
  Collaborating,
  Drafts,
  Archived,
}

export interface CreatePitchPayload {
  avatar: string;
  eventId: string;
  title: string;
  email: string;
  position: number;
  requestDocument: boolean;
}

export interface EventLocation {
  fullAddress: string;
  street: null | string;
  city: null | string;
  state: null | string;
  zip: null | string;
  country: null | string;
  latitude: string;
  longitude: string;
  placeId: string;
  tba: boolean;
}

export type ComputedSession = AgendaInterface & {
  computedStart: Date;
  computedEnd: Date;
};

export interface ManageAgendaInterface {
  agendaType: string;
  teamId?: string;
  eventId: string;
  position: number;
  title: string;
  description: string;
  start?: string;
  end: string;
  duration: number;
  visible?: boolean;
  location?: EventLocation | null;
  debrief?: number;
}

export interface PitchInterface {
  eventId: string;
  sessionId: string;
  title: string;
  position: number;
  avatarUrl: null | string;
  requestDocument: boolean;
  dateCreated: string;
  id: string;
}

export type HourString =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "11"
  | "12"
  | "13"
  | "14"
  | "15"
  | "16"
  | "17"
  | "18"
  | "19"
  | "20"
  | "21"
  | "22"
  | "23";

export interface SalesByDate {
  [date: string]: {
    [ticketType: string]: number; // dynamic ticket type keys
  };
}

export type HourlySalesByString = Record<HourString, number>;

export interface TicketInsightTable {
  ticketName: string;
  date: string;
  amount: number;
  discountedAmount: number;
  ticketId: string;
  ticketOrderId: string;
  eventId: string;
  formId: null | string;
  formResponseId: null | string;
  email: string;
  name: string;
  phoneNumber: string;
  formResponses: string | null;
  id: string;
}

export interface ResolveAgendaTypeInterface {
  label: string;
  type: SpeakerType;
  active: boolean;
}

export type PresenterActiveTab =
  | "note"
  | "people"
  | "question"
  | "poll"
  | "share"
  | "agenda"
  | "playlist";

export enum EventEnum {
  Scheduled,
  Started,
  Ended,
}

export enum PresentationEnum {
  Created,
  Scheduled,
  Presenting,
  Paused,
  Ended,
}

export interface HandleAddLineupToSession {
  payload: {
    speakerId: string[];
    sessionId: string;
    lineUpType: number;
  };
  eventId: string;
}

// optional: make these aliases to self-document the map keys
export type DateLabel = string; // e.g. "Oct 8"
export type TicketName = string; // e.g. "Express Ticket"

// per-ticket stats
export interface TicketStats {
  count: number; // quantity
  sales: number; // amount
}

// NEW overall shape
export type SalesByDateV2 = Record<DateLabel, Record<TicketName, TicketStats>>;

export enum PollEnum {
  Draft,
  Published,
  Started,
  Running,
  Completed,
}

export interface EventSettingInterface {
  allowQuestion: boolean;
  enableWaitingRoom: boolean;
  enableSummary: boolean;
  enableConversation: boolean;
  authMode: 0;
  projectorControl: boolean;
  displayInstruction: 0;
  bannerPosition: 0;
  bannerStyle: 0;
  checkedInVote: boolean;
  hasJudges: boolean;
  isPublic: boolean;
  canDownload: number;
  allowPostEventAccess: boolean;
}

export interface EventLinkInterface {
  app: string;
  link: string;
}

export interface FileDetails {
  file: File;
  id: string;
  name: string;
  imgUrl: string;
  size: string;
  mime: string;
  documentId?: string;
  folderId?: string;
}

interface Badge {
  badge: string;
  count: number;
}

export interface SpeakerSettingsResponse {
  id: string;
  name: string;
  description: string;
  company: string;
  designation: string;
  location: string;
  images: string[];
  videos: string[];
  socialMedia: SocialMediaItem[];
  expertise: string[];
  userId: string;
  friendlyId: string;
  socialMediaJson: string;
  dateAdded: string;
  dateModified: string;
  phone: string;
  rating: number;
  availability: boolean;
  badges: Badge[];
  socials: SocialMediaItem[];
  currency: string | null;
  minimumAmount: number;
  maximumAmount: number;
  availableCountries: string[] | null;
  events: number;
  email: string;
  verifiedRating: number;
  attendees: number;
  engagementScore: number;
}

export interface EventEngagement {
  pollResponses: number;
  questionsAsked: number;
  reactions: number;
  comments: number;
}

export interface AgendaMetrics {
  attendees: number;
  averageAttentionScore: number;
  averageTimeSpent: number;
  presentationId: string;
  sessionId: string;
  speakers: number;
  title: string;
}

export interface EventTeam {
  eventId: string;
  email: string;
  name: string;
  role: string;
  dateAdded: string;
  userId: string;
  active: boolean;
  id: string;
}

export interface SummaryInterface {
  summary: string;
  reasoning: string[];
  key_takeaways: string[];
  action_points: string[];
}

export interface EventParticipants {
  sessionId: string;
  email: string;
  name: string;
  invited: boolean;
  checkedIn: boolean;
  status: number;
  accessType: number;
  date: string;
  connectionId: string;
  presentaConnectionId: string;
  joined: string;
  left: string;
  ip: string;
  device: string;
  ipData: string;
  avatar: string;
  country: string;
  city: string;
  id: string;
}

export interface ParticipantTicket {
  ticketName: string;
  currency: string;
  date: string;
  eventDate: string;
  amount: number;
  discountedAmount: number;
  ticketId: string;
  ticketOrderId: string;
  eventId: string;
  eventName: string;
  eventLocation: EventLocation;
  formId: string | null;
  formResponseId: string | null;
  email: string;
  name: string;
  phoneNumber: string;
  orderedBy: string;
  profilePic: string;
  appleWallet: string | null;
  googleWallet: string | null;
  formResponses: string | null;
  id: string;
  orderId: string;
}
